import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { checkNCBStatus } from '../api';

const BackendStatusContext = createContext(null);

export const BackendStatusProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(true);
  const [isChecking, setIsChecking] = useState(false);
  const [lastError, setLastError] = useState(null);
  const [lastCheckTime, setLastCheckTime] = useState(null);
  const [hasCheckedOnce, setHasCheckedOnce] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const performCheck = useCallback(async () => {
    setIsChecking(true);
    setLastError(null);
    try {
      const result = await checkNCBStatus();
      setIsConnected(result.connected);
      setLastError(result.error);
      if (result.connected) {
        setDismissed(false);
      }
    } catch (err) {
      setIsConnected(false);
      setLastError(err?.message || 'Unexpected error');
    } finally {
      setLastCheckTime(Date.now());
      setHasCheckedOnce(true);
      setIsChecking(false);
    }
  }, []);

  // Initial delayed check + 30 s periodic re-check
  useEffect(() => {
    const initialTimer = setTimeout(performCheck, 2000);
    const interval = setInterval(performCheck, 30000);
    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, [performCheck]);

  const value = {
    isConnected,
    isChecking,
    lastError,
    lastCheckTime,
    hasCheckedOnce,
    dismissed,
    setDismissed,
    checkNow: performCheck,
  };

  return (
    <BackendStatusContext.Provider value={value}>
      {children}
    </BackendStatusContext.Provider>
  );
};

export const useBackendStatus = () => {
  const context = useContext(BackendStatusContext);
  if (!context) {
    throw new Error('useBackendStatus must be used within a BackendStatusProvider');
  }
  return context;
};